/**
 * Step1DataRefresh.js
 *
 * WHAT: Step 1 of the campaign workflow -- verifies and refreshes source data freshness.
 * WHY:  Attestation decisions must be based on current data. This step ensures enterprise
 *       users (from Azure AD), AD group memberships, and database user access records are
 *       all recent enough before the campaign proceeds. Stale data would cause managers
 *       to review outdated access, undermining the compliance value of the attestation.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';
import { useSafeTimeout } from '../../hooks';
import { WideModal, CertificationStatusModal } from '../common';

/**
 * WHAT: Formats a numeric hour value into a human-readable "N days and M hours" string.
 * WHY:  Data freshness thresholds are stored in hours but displayed to users in a
 *       friendlier days-and-hours format.
 */
// Helper function to format hours into "n days and nn hours" format
const formatHoursAsDaysAndHours = (totalHours) => {
  const days = Math.floor(totalHours / 24);
  const hours = Math.round(totalHours % 24);

  if (days === 0) {
    return `${hours}h`;
  } else if (hours === 0) {
    return `${days} day${days !== 1 ? 's' : ''}`;
  } else {
    return `${days} day${days !== 1 ? 's' : ''} and ${hours}h`;
  }
};

/**
 * WHAT: Data refresh panel showing freshness status for enterprise users, AD groups, and database users.
 * WHY:  Lets campaign creators verify that source data meets freshness thresholds and trigger
 *       on-demand syncs before building the attestation scope, ensuring compliance accuracy.
 */
const Step1DataRefresh = ({ campaign, activeStep }) => {
  // Safe timeout hook for auto-dismissing messages
  const { setSafeTimeout } = useSafeTimeout();

  // Enterprise Users State
  const [refreshInfo, setRefreshInfo] = useState(null);
  const [isLoadingRefreshInfo, setIsLoadingRefreshInfo] = useState(false);
  const [refreshInfoError, setRefreshInfoError] = useState(null);
  const [isSyncingEnterpriseUsers, setIsSyncingEnterpriseUsers] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  // Replaces the blocking window.confirm() before the Azure AD users sync with a
  // WideModal so the confirmation matches the app's modal styling.
  const [showEnterpriseSyncConfirm, setShowEnterpriseSyncConfirm] = useState(false);

  // Azure AD Groups State
  const [adGroupsInfo, setAdGroupsInfo] = useState(null);
  const [isLoadingAdGroupsInfo, setIsLoadingAdGroupsInfo] = useState(false);
  const [isSyncingAdGroups, setIsSyncingAdGroups] = useState(false);
  const [adGroupsSyncMessage, setAdGroupsSyncMessage] = useState(null);
  // Replaces the blocking window.confirm() before the AD Groups sync with a
  // WideModal so the confirmation matches the app's modal styling.
  const [showAdGroupsSyncConfirm, setShowAdGroupsSyncConfirm] = useState(false);

  // Database Users State
  const [databaseUsersInfo, setDatabaseUsersInfo] = useState(null);
  const [isLoadingDatabaseUsers, setIsLoadingDatabaseUsers] = useState(false);
  const [isRefreshingDatabaseUsers, setIsRefreshingDatabaseUsers] = useState(false);
  const [databaseUsersMessage, setDatabaseUsersMessage] = useState('');
  const [databaseUsersError, setDatabaseUsersError] = useState(null);
  const [selectedDatabasesForRefresh, setSelectedDatabasesForRefresh] = useState([]);

  // Certification gate for Step 1 refresh: a RUNNING campaign is still mutable
  // (only ATTESTATION locks it), so privilege/role assignment + certification
  // must be re-verified before every refresh. Mirrors the campaign-creation
  // gate in UARCampaigns so the same blocker logic applies to both call sites.
  const [showCertificationErrorModal, setShowCertificationErrorModal] = useState(false);
  const [certificationError, setCertificationError] = useState({});

  // Progress Modal State
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressStatus, setProgressStatus] = useState('idle'); // idle, running, success, error, cancelled
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressDetails, setProgressDetails] = useState(null);
  const [currentExecutionId, setCurrentExecutionId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  // Per-database / per-step rows (RefreshExecutionStep). One row per (database, step)
  // — pipeline yields 5 step rows per source database (Collect → Update Privilege
  // Reference → Categorize Roles → Categorize Users → Enrich User Statistics).
  // Polled alongside the execution summary so the modal can show fine-grained
  // progress, not just aggregate counters.
  const [executionSteps, setExecutionSteps] = useState([]);
  const [expandedDatabases, setExpandedDatabases] = useState({});

  // Fetch Enterprise Users Info
  const fetchRefreshInfo = async () => {
    setIsLoadingRefreshInfo(true);
    setRefreshInfoError(null);
    try {
      const params = new URLSearchParams();
      if (campaign.compliance_framework) {
        params.append('compliance_framework', campaign.compliance_framework);
      }
      if (campaign.database_type) {
        params.append('database_type', campaign.database_type);
      }
      params.append('_t', Date.now());

      const url = API_CONFIG.getEnterpriseUsersEndpoint(`refresh-info/?${params.toString()}`);
      console.log('Fetching enterprise users info from:', url);
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Enterprise users info response:', data);

      if (data.success) {
        setRefreshInfo(data.refresh_info);
        setRefreshInfoError(null);
      } else {
        const errorMsg = data.error || 'Unknown error occurred';
        setRefreshInfoError(errorMsg);
        console.error('API returned error:', errorMsg);
      }
    } catch (error) {
      const errorMsg = error.message || 'Failed to fetch enterprise users info';
      console.error('Error fetching refresh info:', error);
      setRefreshInfoError(errorMsg);
    } finally {
      setIsLoadingRefreshInfo(false);
    }
  };

  // Handle Enterprise Users Sync.
  // Opens the WideModal confirmation; the actual sync runs in
  // confirmEnterpriseUsersSync once the user clicks Continue.
  const handleEnterpriseUsersSync = () => {
    setShowEnterpriseSyncConfirm(true);
  };

  const confirmEnterpriseUsersSync = async () => {
    setShowEnterpriseSyncConfirm(false);

    try {
      setIsSyncingEnterpriseUsers(true);
      setSyncMessage({ type: 'info', text: 'Syncing users from Azure AD...' });

      const url = API_CONFIG.getEnterpriseUsersEndpoint('azure-ad/sync/');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setSyncMessage({
          type: 'success',
          text: `Successfully synced ${data.data.users_synced} of ${data.data.users_fetched} users from Azure AD`
        });
        await fetchRefreshInfo();
      } else {
        setSyncMessage({
          type: 'error',
          text: data.message || 'Failed to sync from Azure AD'
        });
      }
    } catch (err) {
      console.error('Azure AD sync error:', err);
      setSyncMessage({
        type: 'error',
        text: 'Failed to sync from Azure AD'
      });
    } finally {
      setIsSyncingEnterpriseUsers(false);
      setSafeTimeout(() => setSyncMessage(null), 5000);
    }
  };

  // Fetch AD Groups Info
  const fetchAdGroupsInfo = async () => {
    setIsLoadingAdGroupsInfo(true);
    try {
      const url = API_CONFIG.getDatabasesEndpoint('azure-ad/groups/refresh-status/');
      console.log('Fetching AD Groups info from:', url);
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('AD Groups info response:', data);

      if (data.success) {
        setAdGroupsInfo(data);
      } else {
        console.error('AD Groups API returned error:', data.error);
      }
    } catch (error) {
      console.error('Error fetching AD groups info:', error);
    } finally {
      setIsLoadingAdGroupsInfo(false);
    }
  };

  // Handle AD Groups Sync.
  // Opens the WideModal confirmation; the actual sync runs in
  // confirmAdGroupsSync once the user clicks Continue.
  const handleAdGroupsSync = () => {
    setShowAdGroupsSyncConfirm(true);
  };

  const confirmAdGroupsSync = async () => {
    setShowAdGroupsSyncConfirm(false);

    try {
      setIsSyncingAdGroups(true);
      setAdGroupsSyncMessage({ type: 'info', text: 'Syncing Azure AD Groups...' });

      const url = API_CONFIG.getDatabasesEndpoint('azure-ad/groups/all/sync/');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setAdGroupsSyncMessage({
          type: 'success',
          text: `Successfully synced ${data.sync_results?.groups_synced || 0} groups. Members added: ${data.sync_results?.members_added || 0}, removed: ${data.sync_results?.members_removed || 0}`
        });
        await fetchAdGroupsInfo();
      } else {
        setAdGroupsSyncMessage({
          type: 'error',
          text: data.error || 'Failed to sync Azure AD Groups'
        });
      }
    } catch (err) {
      console.error('Azure AD Groups sync error:', err);
      setAdGroupsSyncMessage({
        type: 'error',
        text: 'Failed to sync Azure AD Groups'
      });
    } finally {
      setIsSyncingAdGroups(false);
      setSafeTimeout(() => setAdGroupsSyncMessage(null), 5000);
    }
  };

  // Fetch Database Users Info
  const fetchDatabaseUsersInfo = async () => {
    setIsLoadingDatabaseUsers(true);
    setDatabaseUsersError(null);
    try {
      const params = new URLSearchParams();
      if (campaign.campaign_id) {
        params.append('campaign_id', campaign.campaign_id);
      }
      if (campaign.compliance_framework) {
        params.append('compliance_framework', campaign.compliance_framework);
      }
      if (campaign.database_type) {
        params.append('database_type', campaign.database_type);
      }
      params.append('_t', Date.now());

      const url = API_CONFIG.getDatabasesEndpoint(`database-users/refresh-info/?${params.toString()}`);
      console.log('Fetching database users info from:', url);
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-store, no-cache, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Database users info response:', data);

      if (data.success) {
        setDatabaseUsersInfo(data.refresh_info);
        setDatabaseUsersError(null);
      } else {
        const errorMsg = data.error || 'Unknown error occurred';
        setDatabaseUsersError(errorMsg);
        console.error('API returned error:', errorMsg);
      }
    } catch (error) {
      const errorMsg = error.message || 'Failed to fetch database users info';
      console.error('Error fetching database users info:', error);
      setDatabaseUsersError(errorMsg);
    } finally {
      setIsLoadingDatabaseUsers(false);
    }
  };

  // Poll for refresh job completion
  const pollRefreshJobStatus = async (executionId, maxAttempts = 60) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Fetch summary + per-step detail in parallel so the modal can update
        // both aggregate counters and the per-database breakdown each tick.
        const [summaryResp, stepsResp] = await Promise.all([
          fetch(
            API_CONFIG.getDatabasesEndpoint(`refresh/v2/executions/${executionId}/`),
            { headers: { 'Cache-Control': 'no-cache' } }
          ),
          fetch(
            API_CONFIG.getDatabasesEndpoint(`refresh/v2/executions/${executionId}/steps/`),
            { headers: { 'Cache-Control': 'no-cache' } }
          ),
        ]);
        const data = await summaryResp.json();
        const stepsData = stepsResp.ok ? await stepsResp.json() : null;
        if (stepsData && stepsData.success && Array.isArray(stepsData.steps)) {
          setExecutionSteps(stepsData.steps);
        }

        if (data.success) {
          const status = data.execution.execution_status;
          const progress = data.execution.progress_percentage || 0;

          // Update progress modal
          setProgressPercentage(progress);
          setProgressMessage(`Refreshing data from source databases... ${progress}% complete`);
          setProgressDetails({
            databases_succeeded: data.execution.databases_succeeded || 0,
            databases_failed: data.execution.databases_failed || 0,
            total_databases: data.execution.total_databases || 0,
            total_users_refreshed: data.execution.total_users_refreshed || 0
          });

          if (status === 'COMPLETED') {
            return { success: true, execution: data.execution };
          } else if (status === 'FAILED') {
            return { success: false, error: data.execution.error_message || 'Job failed' };
          } else if (status === 'CANCELLED') {
            return { success: false, cancelled: true, error: 'Job cancelled by user' };
          }
          await new Promise(resolve => setTimeout(resolve, 2000));
        } else {
          return { success: false, error: data.error };
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    return { success: false, error: 'Refresh job timed out' };
  };

  // Trigger actual database refresh job
  const refreshDatabaseUsers = async (databaseNames = null) => {
    if (!campaign.database_type || !campaign.compliance_framework) {
      setDatabaseUsersMessage('Error: Campaign database type and compliance framework are required');
      return;
    }

    // Re-check certification status before refreshing. The campaign-creation
    // flow gates on this same endpoint; a RUNNING campaign whose privileges
    // drift back to UNASSIGNED/uncertified after creation would otherwise
    // refresh source data into a still-uncertified matrix, producing inaccurate
    // Stage-4 user levels in the eventual report.
    try {
      const certificationCheck = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/check-certification/'),
        { params: { database_type: campaign.database_type } }
      );
      if (!certificationCheck.data.all_certified) {
        setCertificationError({
          databaseType: campaign.database_type,
          uncertifiedCount: certificationCheck.data.uncertified_count,
          certifiedCount: certificationCheck.data.certified_count,
          totalCount: certificationCheck.data.total_count,
          roleUncertifiedCount: certificationCheck.data.role_uncertified_count || 0,
          roleUnassignedCount: certificationCheck.data.role_unassigned_count || 0,
          roleCertifiedCount: certificationCheck.data.role_certified_count || 0,
          roleTotalCount: certificationCheck.data.role_total_count || 0,
        });
        setShowCertificationErrorModal(true);
        return;
      }
    } catch (err) {
      console.error('Certification status check failed:', err);
      setDatabaseUsersMessage('Error: Unable to verify privilege certification status. Try again.');
      return;
    }

    // Show progress modal
    setShowProgressModal(true);
    setProgressStatus('running');
    setProgressPercentage(0);
    setProgressMessage('Starting database refresh job...');
    setProgressDetails(null);
    setExecutionSteps([]);
    setExpandedDatabases({});
    setCurrentExecutionId(null);
    setIsCancelling(false);
    setIsRefreshingDatabaseUsers(true);

    try {
      const requestBody = {
        database_type: campaign.database_type,
        compliance_framework: campaign.compliance_framework,
        triggered_by: 'campaign_workflow'
      };

      if (databaseNames && databaseNames.length > 0) {
        requestBody.database_names_filter = databaseNames;
      }

      const response = await fetch(API_CONFIG.getDatabasesEndpoint('refresh/v2/quick-refresh/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.success) {
        setCurrentExecutionId(data.execution_id);
        setProgressMessage(`Refresh job started: ${data.job_name}. Collecting data from source databases...`);

        const result = await pollRefreshJobStatus(data.execution_id);

        if (result.cancelled) {
          setProgressStatus('cancelled');
          setProgressMessage('Job cancelled by user.');
          fetchDatabaseUsersInfo();
        } else if (result.success) {
          const stats = result.execution.total_users_refreshed || 0;
          setProgressStatus('success');
          setProgressPercentage(100);
          setProgressMessage(`Database refresh completed successfully!`);
          setProgressDetails({
            databases_succeeded: result.execution.databases_succeeded || 0,
            databases_failed: result.execution.databases_failed || 0,
            total_databases: result.execution.total_databases || 0,
            total_users_refreshed: stats
          });
          setSelectedDatabasesForRefresh([]);
          // Refresh the stats in the background
          fetchDatabaseUsersInfo();
        } else {
          setProgressStatus('error');
          setProgressMessage(`Error: ${result.error}`);
        }
      } else {
        if (data.error && data.error.includes('No job definition exists')) {
          setProgressStatus('error');
          setProgressMessage(`No refresh job configured for ${campaign.database_type} - ${campaign.compliance_framework}.`);
          fetchDatabaseUsersInfo();
        } else {
          setProgressStatus('error');
          setProgressMessage(`Error: ${data.error}`);
        }
      }
    } catch (error) {
      console.error('Error triggering database refresh:', error);
      setProgressStatus('error');
      setProgressMessage(`Error: Failed to trigger database refresh - ${error.message}`);
    } finally {
      setIsRefreshingDatabaseUsers(false);
    }
  };

  // Close progress modal and reset state
  const closeProgressModal = () => {
    setShowProgressModal(false);
    setProgressStatus('idle');
    setProgressPercentage(0);
    setProgressMessage('');
    setProgressDetails(null);
    setExecutionSteps([]);
    setExpandedDatabases({});
    setCurrentExecutionId(null);
    setIsCancelling(false);
  };

  // Cancel running refresh job
  const cancelRefreshJob = async () => {
    if (!currentExecutionId) return;
    setIsCancelling(true);
    try {
      await fetch(
        API_CONFIG.getDatabasesEndpoint(`refresh/v2/executions/${currentExecutionId}/cancel/`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      // Polling loop will pick up CANCELLED status and exit
    } catch (error) {
      console.error('Error cancelling job:', error);
      setIsCancelling(false);
    }
  };

  // Quick reload stats from PostgreSQL
  const reloadDatabaseStats = async () => {
    setIsRefreshingDatabaseUsers(true);
    setDatabaseUsersMessage('Reloading statistics...');
    try {
      await fetchDatabaseUsersInfo();
      setDatabaseUsersMessage('Statistics reloaded from cache.');
      setSafeTimeout(() => {
        setDatabaseUsersMessage('');
      }, 5000);
    } catch (error) {
      setDatabaseUsersMessage(`Error: ${error.message}`);
    } finally {
      setIsRefreshingDatabaseUsers(false);
    }
  };

  // Load data when component mounts or activeStep changes to 1
  useEffect(() => {
    if (activeStep === 1) {
      fetchRefreshInfo();
      fetchAdGroupsInfo();
      fetchDatabaseUsersInfo();
    }
  }, [activeStep]);

  return (
    <div className="mt-4">
      <div className="row">
        {/* Left Panel - Enterprise Users & AD Groups */}
        <div className="col-lg-6">
          {/* Enterprise Users Section */}
          <div className="p-3 bg-info-subtle border border-info rounded mb-3">
            <div className="mb-3">
              <h6 className="fw-semibold mb-0">
                <i className="fas fa-users text-info me-2"></i>
                Enterprise Users
              </h6>
              {refreshInfo && refreshInfo.hours_since_refresh !== null && (
                <div className={`mt-2 p-2 rounded ${refreshInfo.hours_since_refresh < 24 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                  <i className={`fas ${refreshInfo.hours_since_refresh < 24 ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                  {refreshInfo.hours_since_refresh < 24 ? (
                    <span><strong>Current</strong> - refreshed {Math.round(refreshInfo.hours_since_refresh)}h ago</span>
                  ) : (
                    <span><strong>Stale</strong> - {formatHoursAsDaysAndHours(refreshInfo.hours_since_refresh)} old</span>
                  )}
                </div>
              )}
            </div>
            {isLoadingRefreshInfo ? (
              <div className="d-flex align-items-center text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Loading...
              </div>
            ) : refreshInfo ? (
              <div>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-users text-primary me-2"></i>
                    <strong>Total:</strong>
                    <span className="badge bg-primary ms-2">{refreshInfo.total_users}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user-check text-success me-2"></i>
                    <strong>Active:</strong>
                    <span className="badge bg-success ms-2">{refreshInfo.active_users}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user-times text-danger me-2"></i>
                    <strong>Inactive:</strong>
                    <span className="badge bg-danger ms-2">{refreshInfo.inactive_users}</span>
                  </div>
                </div>
                <button
                  className={`btn btn-sm ${refreshInfo.hours_since_refresh < 24 ? 'btn-success' : 'btn-warning'}`}
                  onClick={handleEnterpriseUsersSync}
                  disabled={isSyncingEnterpriseUsers}
                >
                  {isSyncingEnterpriseUsers ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt me-2"></i>
                      Refresh Now
                    </>
                  )}
                </button>
                {syncMessage && (
                  <div className={`alert alert-${syncMessage.type === 'success' ? 'success' : 'danger'} mt-2 mb-0 py-2`}>
                    <small><i className={`fas ${syncMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>{syncMessage.text}</small>
                  </div>
                )}
              </div>
            ) : (
              <div className="alert alert-warning mb-0" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Unable to load status
                {refreshInfoError && <div className="small mt-1">Error: {refreshInfoError}</div>}
              </div>
            )}
          </div>

          {/* Azure AD Groups Section */}
          <div className="p-3 bg-info-subtle border border-info rounded mb-3">
            <div className="mb-3">
              <h6 className="fw-semibold mb-0">
                <i className="fas fa-users-cog text-info me-2"></i>
                Azure AD Groups
              </h6>
              {adGroupsInfo && adGroupsInfo.hours_since_refresh !== null && (
                <div className={`mt-2 p-2 rounded ${adGroupsInfo.hours_since_refresh < 24 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                  <i className={`fas ${adGroupsInfo.hours_since_refresh < 24 ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                  {adGroupsInfo.hours_since_refresh < 24 ? (
                    <span><strong>Current</strong> - synced {Math.round(adGroupsInfo.hours_since_refresh)}h ago</span>
                  ) : (
                    <span><strong>Stale</strong> - {formatHoursAsDaysAndHours(adGroupsInfo.hours_since_refresh)} old</span>
                  )}
                </div>
              )}
            </div>
            {isLoadingAdGroupsInfo ? (
              <div className="d-flex align-items-center text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Loading...
              </div>
            ) : adGroupsInfo ? (
              <div>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-layer-group text-primary me-2"></i>
                    <strong>Groups:</strong>
                    <span className="badge bg-primary ms-2">{adGroupsInfo.total_groups}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-users text-info me-2"></i>
                    <strong>Total Members:</strong>
                    <span className="badge bg-info ms-2">{adGroupsInfo.total_members}</span>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user-check text-success me-2"></i>
                    <strong>Active:</strong>
                    <span className="badge bg-success ms-2">{adGroupsInfo.active_members}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user-times text-danger me-2"></i>
                    <strong>Inactive:</strong>
                    <span className="badge bg-danger ms-2">{(adGroupsInfo.total_members || 0) - (adGroupsInfo.active_members || 0)}</span>
                  </div>
                </div>
                <button
                  className={`btn btn-sm ${adGroupsInfo.hours_since_refresh < 24 ? 'btn-success' : 'btn-warning'}`}
                  onClick={handleAdGroupsSync}
                  disabled={isSyncingAdGroups}
                >
                  {isSyncingAdGroups ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt me-2"></i>
                      Refresh Now
                    </>
                  )}
                </button>
                {adGroupsSyncMessage && (
                  <div className={`alert alert-${adGroupsSyncMessage.type === 'success' ? 'success' : 'danger'} mt-2 mb-0 py-2`}>
                    <small><i className={`fas ${adGroupsSyncMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>{adGroupsSyncMessage.text}</small>
                  </div>
                )}
              </div>
            ) : (
              <div className="alert alert-warning mb-0" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Unable to load AD Groups status
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Database Users */}
        <div className="col-lg-6">
          <div className="p-3 bg-info-subtle border border-info rounded mb-3" style={{ height: '100%' }}>
            <div className="mb-3">
              <h6 className="fw-semibold mb-0">
                <i className="fas fa-database text-info me-2"></i>
                Database Users
              </h6>
              {databaseUsersInfo && databaseUsersInfo.last_refresh_time && (
                <small className="text-muted">
                  <i className="fas fa-clock me-1"></i>
                  Last Refreshed: {new Date(databaseUsersInfo.last_refresh_time).toLocaleString()}
                </small>
              )}
            </div>
            {isLoadingDatabaseUsers ? (
              <div className="d-flex align-items-center text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Loading database users...
              </div>
            ) : databaseUsersInfo ? (
              <div>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-users-cog text-primary me-2"></i>
                    <strong>Total Users:</strong>
                    <span className="badge bg-primary ms-2">{databaseUsersInfo.total_users}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user-check text-success me-2"></i>
                    <strong>Active:</strong>
                    <span className="badge bg-success ms-2">{databaseUsersInfo.active_users}</span>
                  </div>
                  {/* Show Roles for Cassandra/PostgreSQL/MSSQL (their snapshots include roles
                      as principals); show Inactive for Oracle/MySQL where roles aren't blended. */}
                  {['CASSANDRA', 'POSTGRESQL', 'MSSQL'].includes(campaign.database_type) ? (
                    <>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-user-tag text-secondary me-2"></i>
                        <strong>Roles:</strong>
                        <span className="badge bg-secondary ms-2">{databaseUsersInfo.total_roles || 0}</span>
                      </div>
                      {/* MSSQL still has disabled logins worth surfacing. */}
                      {campaign.database_type === 'MSSQL' && (
                        <div className="d-flex align-items-center">
                          <i className="fas fa-user-times text-danger me-2"></i>
                          <strong>Inactive:</strong>
                          <span className="badge bg-danger ms-2">{databaseUsersInfo.inactive_users}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="d-flex align-items-center">
                      <i className="fas fa-user-times text-danger me-2"></i>
                      <strong>Inactive:</strong>
                      <span className="badge bg-danger ms-2">{databaseUsersInfo.inactive_users}</span>
                    </div>
                  )}
                </div>

                {/* Database Name Groups - Compact Table with Checkboxes */}
                {databaseUsersInfo.database_name_groups && databaseUsersInfo.database_name_groups.length > 0 && (
                  <div className="mb-3 pt-2 border-top">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted fw-semibold">Select databases to refresh:</small>
                      <div>
                        <button
                          className="btn btn-link btn-sm p-0 me-2"
                          onClick={() => setSelectedDatabasesForRefresh(
                            databaseUsersInfo.database_name_groups.map(g => g.database_name)
                          )}
                        >
                          Select All
                        </button>
                        <button
                          className="btn btn-link btn-sm p-0"
                          onClick={() => setSelectedDatabasesForRefresh([])}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="table-responsive mt-1" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <table className="table table-sm table-hover mb-0">
                        <thead className="sticky-top" style={{ backgroundColor: '#e9ecef' }}>
                          <tr>
                            <th style={{ fontSize: '0.75rem', fontWeight: '600', color: '#212529', backgroundColor: '#e9ecef' }}>
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                checked={selectedDatabasesForRefresh.length === databaseUsersInfo.database_name_groups.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDatabasesForRefresh(
                                      databaseUsersInfo.database_name_groups.map(g => g.database_name)
                                    );
                                  } else {
                                    setSelectedDatabasesForRefresh([]);
                                  }
                                }}
                              />
                              Database
                            </th>
                            <th className="text-center" style={{ fontSize: '0.75rem', fontWeight: '600', color: '#212529', backgroundColor: '#e9ecef' }}>Users</th>
                            <th className="text-center" style={{ fontSize: '0.75rem', fontWeight: '600', color: '#212529', backgroundColor: '#e9ecef' }}>Last Refreshed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {databaseUsersInfo.database_name_groups.map((group, index) => {
                            const formatLastRefreshed = (dateStr) => {
                              if (!dateStr) return 'Never';
                              const date = new Date(dateStr);
                              const now = new Date();
                              const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
                              if (diffHours < 1) return 'Just now';
                              if (diffHours < 24) return `${diffHours}h ago`;
                              const diffDays = Math.floor(diffHours / 24);
                              if (diffDays === 1) return '1 day ago';
                              if (diffDays < 7) return `${diffDays} days ago`;
                              return date.toLocaleDateString();
                            };
                            const getRefreshBadgeColor = (dateStr) => {
                              if (!dateStr) return 'bg-secondary';
                              const diffHours = (new Date() - new Date(dateStr)) / (1000 * 60 * 60);
                              if (diffHours <= 24) return 'bg-success';
                              if (diffHours <= 168) return 'bg-warning';
                              return 'bg-danger';
                            };
                            return (
                              <tr key={index} style={{ fontSize: '0.8rem' }}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input me-2"
                                    checked={selectedDatabasesForRefresh.includes(group.database_name)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedDatabasesForRefresh([...selectedDatabasesForRefresh, group.database_name]);
                                      } else {
                                        setSelectedDatabasesForRefresh(selectedDatabasesForRefresh.filter(n => n !== group.database_name));
                                      }
                                    }}
                                  />
                                  {group.database_name}
                                </td>
                                <td className="text-center">{group.user_count}</td>
                                <td className="text-center">
                                  <span className={`badge ${getRefreshBadgeColor(group.last_refreshed)}`} title={group.last_refreshed ? new Date(group.last_refreshed).toLocaleString() : 'Never'}>
                                    {formatLastRefreshed(group.last_refreshed)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="d-flex gap-2 flex-wrap">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => refreshDatabaseUsers(selectedDatabasesForRefresh.length > 0 ? selectedDatabasesForRefresh : null)}
                    disabled={isRefreshingDatabaseUsers}
                    title="Connect to source databases and collect fresh user data"
                  >
                    {isRefreshingDatabaseUsers ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-database me-2"></i>
                        {selectedDatabasesForRefresh.length > 0
                          ? `Collect from Selected (${selectedDatabasesForRefresh.length})`
                          : 'Collect from Source'}
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={reloadDatabaseStats}
                    disabled={isRefreshingDatabaseUsers}
                    title="Reload statistics from cache (no database connection)"
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Reload Stats
                  </button>
                </div>
                {databaseUsersMessage && (
                  <div className="alert alert-info mt-2 mb-0 py-2">
                    <small><i className="fas fa-info-circle me-2"></i>{databaseUsersMessage}</small>
                  </div>
                )}
              </div>
            ) : (
              <div className="alert alert-warning mb-0" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Unable to load database users status
                {databaseUsersError && <div className="small mt-1">Error: {databaseUsersError}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Modal for Database Refresh */}
      {showProgressModal && (
        <div className="modal fade show" style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1050,
          overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className={`modal-header ${
                progressStatus === 'success' ? 'bg-success' :
                progressStatus === 'error' ? 'bg-danger' :
                progressStatus === 'cancelled' ? 'bg-warning' :
                'bg-primary'
              } text-white`}>
                <h5 className="modal-title">
                  <i className={`fas ${
                    progressStatus === 'success' ? 'fa-check-circle' :
                    progressStatus === 'error' ? 'fa-exclamation-circle' :
                    progressStatus === 'cancelled' ? 'fa-ban' :
                    'fa-database'
                  } me-2`}></i>
                  {progressStatus === 'success' ? 'Refresh Complete' :
                   progressStatus === 'error' ? 'Refresh Failed' :
                   progressStatus === 'cancelled' ? 'Refresh Cancelled' :
                   'Collecting from Source'}
                </h5>
                {(progressStatus === 'success' || progressStatus === 'error' || progressStatus === 'cancelled') && (
                  <button type="button" className="btn-close btn-close-white" onClick={closeProgressModal}></button>
                )}
              </div>
              <div className="modal-body">
                {/* Progress Bar */}
                {progressStatus === 'running' && (
                  <div className="mb-3">
                    <div className="progress" style={{ height: '25px' }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                        role="progressbar"
                        style={{ width: `${progressPercentage}%` }}
                        aria-valuenow={progressPercentage}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {progressPercentage}%
                      </div>
                    </div>
                  </div>
                )}

                {/* Status Message */}
                <div className={`alert ${
                  progressStatus === 'success' ? 'alert-success' :
                  progressStatus === 'error' ? 'alert-danger' :
                  progressStatus === 'cancelled' ? 'alert-warning' :
                  'alert-info'
                } mb-3`}>
                  <i className={`fas ${
                    progressStatus === 'success' ? 'fa-check-circle' :
                    progressStatus === 'error' ? 'fa-exclamation-circle' :
                    progressStatus === 'cancelled' ? 'fa-ban' :
                    'fa-spinner fa-spin'
                  } me-2`}></i>
                  {progressMessage}
                </div>

                {/* Details */}
                {progressDetails && (
                  <div className="card bg-light">
                    <div className="card-body py-2">
                      <div className="row text-center">
                        <div className="col-6 col-md-3 mb-2">
                          <div className="fw-bold text-primary">{progressDetails.total_databases}</div>
                          <small className="text-muted">Databases</small>
                        </div>
                        <div className="col-6 col-md-3 mb-2">
                          <div className="fw-bold text-success">{progressDetails.databases_succeeded}</div>
                          <small className="text-muted">Succeeded</small>
                        </div>
                        <div className="col-6 col-md-3 mb-2">
                          <div className="fw-bold text-danger">{progressDetails.databases_failed}</div>
                          <small className="text-muted">Failed</small>
                        </div>
                        <div className="col-6 col-md-3 mb-2">
                          <div className="fw-bold text-info">{progressDetails.total_users_refreshed}</div>
                          <small className="text-muted">Users</small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Per-database step breakdown. Steps stream in from
                    /refresh/v2/executions/<id>/steps/ during polling so users
                    can see exactly which database + pipeline stage is running. */}
                {executionSteps.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong className="small text-muted text-uppercase">
                        <i className="fas fa-list-ol me-2"></i>
                        Per-Database Progress
                      </strong>
                      <div>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 me-3"
                          onClick={() => {
                            const all = {};
                            [...new Set(executionSteps.map(s => s.database_name || '(unknown)'))]
                              .forEach(db => { all[db] = true; });
                            setExpandedDatabases(all);
                          }}
                        >Expand all</button>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() => setExpandedDatabases({})}
                        >Collapse all</button>
                      </div>
                    </div>
                    <div className="table-responsive" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th style={{ width: '28px' }}></th>
                            <th>Database</th>
                            <th className="text-center">Steps</th>
                            <th className="text-end">Records</th>
                            <th className="text-end">Duration</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const STEP_ORDER = ['Collect Source Data', 'Update Privilege Reference', 'Categorize Roles', 'Categorize Users', 'Enrich User Statistics'];
                            // Group rows by database_name preserving first-seen order.
                            const groups = new Map();
                            for (const step of executionSteps) {
                              const db = step.database_name || '(unknown)';
                              if (!groups.has(db)) groups.set(db, []);
                              groups.get(db).push(step);
                            }
                            const renderStatusIcon = (status) => {
                              if (status === 'SUCCESS') return <i className="fas fa-check-circle text-success me-1" title="Success"></i>;
                              if (status === 'FAILED') return <i className="fas fa-times-circle text-danger me-1" title="Failed"></i>;
                              if (status === 'RUNNING') return <i className="fas fa-spinner fa-spin text-primary me-1" title="Running"></i>;
                              if (status === 'SKIPPED') return <i className="fas fa-minus-circle text-muted me-1" title="Skipped"></i>;
                              return <i className="fas fa-circle-notch text-muted me-1" title={status || 'Pending'}></i>;
                            };
                            const rows = [];
                            for (const [db, steps] of groups.entries()) {
                              const total = steps.length;
                              const succeeded = steps.filter(s => s.step_status === 'SUCCESS').length;
                              const failed = steps.filter(s => s.step_status === 'FAILED').length;
                              const running = steps.filter(s => s.step_status === 'RUNNING').length;
                              const records = steps.reduce((sum, s) => sum + (s.records_processed || 0), 0);
                              const duration = steps.reduce((sum, s) => sum + (s.duration_seconds || 0), 0);
                              const dbStatus = failed > 0 ? 'failed' : running > 0 ? 'running' : succeeded === total ? 'success' : 'pending';
                              const expanded = !!expandedDatabases[db];
                              rows.push(
                                <tr key={`db-${db}`} style={{ cursor: 'pointer' }} onClick={() => setExpandedDatabases(prev => ({ ...prev, [db]: !prev[db] }))}>
                                  <td><i className={`fas ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'} text-muted`}></i></td>
                                  <td><strong><i className="fas fa-database text-muted me-2"></i>{db}</strong></td>
                                  <td className="text-center">
                                    <span className="text-success me-2">{succeeded}</span>/
                                    <span className="text-muted mx-2">{total}</span>
                                    {failed > 0 && <span className="text-danger ms-1">({failed} failed)</span>}
                                  </td>
                                  <td className="text-end">{records.toLocaleString()}</td>
                                  <td className="text-end text-muted">{duration ? `${duration}s` : '—'}</td>
                                  <td>
                                    {dbStatus === 'success' && <span className="text-success"><i className="fas fa-check-circle me-1"></i>Done</span>}
                                    {dbStatus === 'failed' && <span className="text-danger"><i className="fas fa-times-circle me-1"></i>Failed</span>}
                                    {dbStatus === 'running' && <span className="text-primary"><i className="fas fa-spinner fa-spin me-1"></i>Running</span>}
                                    {dbStatus === 'pending' && <span className="text-muted"><i className="fas fa-clock me-1"></i>In progress</span>}
                                  </td>
                                </tr>
                              );
                              if (expanded) {
                                const stepsByName = new Map(steps.map(s => [s.step_name, s]));
                                const orderedSteps = STEP_ORDER
                                  .map(name => stepsByName.get(name))
                                  .filter(Boolean)
                                  .concat(steps.filter(s => !STEP_ORDER.includes(s.step_name)));
                                rows.push(
                                  <tr key={`db-${db}-steps`} className="bg-light">
                                    <td colSpan="6" className="p-0">
                                      <table className="table table-sm mb-0">
                                        <thead className="table-dark">
                                          <tr>
                                            <th style={{ paddingLeft: '40px' }}>Step</th>
                                            <th>Status</th>
                                            <th className="text-end">Records</th>
                                            <th className="text-end">Duration</th>
                                            <th>Error</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {orderedSteps.map((s) => (
                                            <tr key={`${db}-${s.step_order}`}>
                                              <td style={{ paddingLeft: '40px' }}>
                                                <span className="text-muted me-2">#{s.step_order}</span>
                                                {s.step_name}
                                              </td>
                                              <td>
                                                {renderStatusIcon(s.step_status)}
                                                <small>{s.step_status || 'PENDING'}</small>
                                              </td>
                                              <td className="text-end">{(s.records_processed || 0).toLocaleString()}</td>
                                              <td className="text-end text-muted">{s.duration_seconds != null ? `${s.duration_seconds}s` : '—'}</td>
                                              <td>
                                                {s.error_message && (
                                                  <span className="text-danger small" title={s.error_message}>
                                                    {s.error_message.length > 60 ? `${s.error_message.substring(0, 60)}…` : s.error_message}
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                );
                              }
                            }
                            return rows;
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Spinner for running state */}
                {progressStatus === 'running' && executionSteps.length === 0 && (
                  <div className="text-center mt-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2 mb-0">
                      <small>Please wait while data is being collected from source databases...</small>
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {(progressStatus === 'success' || progressStatus === 'error' || progressStatus === 'cancelled') && (
                  <button type="button" className="btn btn-primary" onClick={closeProgressModal}>
                    Close
                  </button>
                )}
                {progressStatus === 'running' && (
                  <>
                    <small className="text-muted me-auto">
                      <i className="fas fa-info-circle me-1"></i>
                      This window will update automatically as the refresh progresses
                    </small>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={cancelRefreshJob}
                      disabled={isCancelling}
                    >
                      {isCancelling ? (
                        <><i className="fas fa-spinner fa-spin me-1"></i>Cancelling...</>
                      ) : (
                        <><i className="fas fa-times-circle me-1"></i>Cancel Job</>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Azure AD users sync confirmation — replaces window.confirm() */}
      <WideModal
        show={showEnterpriseSyncConfirm}
        onClose={() => setShowEnterpriseSyncConfirm(false)}
        title="Sync Users from Azure AD"
        headerIcon="fa-users-cog"
        maxWidth="560px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowEnterpriseSyncConfirm(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirmEnterpriseUsersSync}
            >
              <i className="fas fa-sync-alt me-1"></i>Continue
            </button>
          </>
        }
      >
        <p className="mb-0">
          This will sync all users from Azure Active Directory. This may take
          several minutes. Continue?
        </p>
      </WideModal>

      {/* Azure AD Groups sync confirmation — replaces window.confirm() */}
      <WideModal
        show={showAdGroupsSyncConfirm}
        onClose={() => setShowAdGroupsSyncConfirm(false)}
        title="Sync Azure AD Groups"
        headerIcon="fa-users-cog"
        maxWidth="560px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowAdGroupsSyncConfirm(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirmAdGroupsSync}
            >
              <i className="fas fa-sync-alt me-1"></i>Continue
            </button>
          </>
        }
      >
        <p className="mb-0">
          This will sync all Azure AD Groups and their memberships. This may take
          several minutes. Continue?
        </p>
      </WideModal>

      {/* Certification gate: blocks Step 1 refresh when privileges/roles for
          this campaign's database_type are unassigned or uncertified. Mirrors
          the campaign-creation gate in UARCampaigns. */}
      <CertificationStatusModal
        show={showCertificationErrorModal}
        onClose={() => setShowCertificationErrorModal(false)}
        variant="error"
        title="Cannot Refresh Database Users"
        databaseType={certificationError.databaseType}
        totalCount={certificationError.totalCount || 0}
        certifiedCount={certificationError.certifiedCount || 0}
        uncertifiedCount={certificationError.uncertifiedCount || 0}
        alertMessage={(() => {
          const pu = certificationError.uncertifiedCount || 0;
          const ru = certificationError.roleUncertifiedCount || 0;
          const rUnassigned = certificationError.roleUnassignedCount || 0;
          const parts = [];
          if (pu > 0) parts.push(<><strong>{pu}</strong> privilege{pu === 1 ? '' : 's'}</>);
          if (ru > 0) parts.push(<><strong>{ru}</strong> role{ru === 1 ? '' : 's'}</>);
          return (
            <>
              {parts.length === 0
                ? <>Certification for <strong>{certificationError.databaseType}</strong> is incomplete.</>
                : <>{parts.reduce((a, b) => <>{a} and {b}</>)} for{' '}
                    <strong>{certificationError.databaseType}</strong>{' '}
                    {(pu + ru) === 1 ? 'is' : 'are'} not yet certified.</>}
              {rUnassigned > 0 && <> <strong>{rUnassigned}</strong> of those role{rUnassigned === 1 ? '' : 's'} still need{rUnassigned === 1 ? 's' : ''} a privilege level assigned before {rUnassigned === 1 ? 'it' : 'they'} can be certified.</>}
              <> A refresh on this campaign would source data into an uncertified privilege matrix, so it is blocked until certification is complete.</>
            </>
          );
        })()}
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowCertificationErrorModal(false)}
          >
            <i className="fas fa-times me-2"></i>
            Close
          </button>
        }
      >
        <div className="alert alert-info mb-0" style={{ border: 'none' }}>
          <div className="d-flex align-items-start">
            <i className="fas fa-lightbulb fa-lg me-3 mt-1"></i>
            <div>
              <h6 className="alert-heading mb-2">Next Steps</h6>
              <ol className="mb-0 ps-3">
                <li className="mb-1">Navigate to <strong>"Admin Settings → Manage Privilege Certification"</strong></li>
                <li className="mb-1">Assign and certify all <strong>{certificationError.databaseType}</strong> privileges and roles</li>
                <li>Return here and refresh again</li>
              </ol>
            </div>
          </div>
        </div>
      </CertificationStatusModal>
    </div>
  );
};

export default Step1DataRefresh;
